/**
 * Structured Data (JSON-LD) Helper Functions
 * Generates Schema.org compliant structured data for better SEO
 */

/**
 * Generate Organization schema for Taealam
 * Used on homepage and about pages
 */
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "name": "Taealam",
  "alternateName": "تعلّم",
  "description": "Online tutoring platform connecting students with expert teachers in Bahrain",
  "url": "https://taealam.bh",
  "logo": "https://taealam.bh/logo.png",
  "sameAs": [
    // Add social media URLs when available
  ],
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "BH",
    "addressRegion": "Bahrain"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Service",
    "availableLanguage": ["English", "Arabic"]
  },
  "areaServed": {
    "@type": "Country",
    "name": "Bahrain"
  }
});

/**
 * Generate Website schema with search action
 * Helps search engines understand site structure
 */
export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Taealam",
  "alternateName": "تعلّم",
  "url": "https://taealam.bh",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://taealam.bh/dashboard/student/teachers?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  },
  "inLanguage": ["en", "ar"]
});

/**
 * Generate Breadcrumb schema for navigation
 * Helps search engines understand site hierarchy
 *
 * @param {Array} items - Array of {name, url} objects
 */
export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://taealam.bh${item.url}`
  }))
});

/**
 * Generate Course schema for teacher offerings
 * Used on teacher profile pages
 *
 * @param {Object} teacher - Teacher data object
 */
export const generateCourseSchema = (teacher) => {
  if (!teacher) return null;

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${teacher.subjects?.join(', ')} Tutoring`,
    "description": `Learn ${teacher.subjects?.join(', ')} with ${teacher.full_name || 'an expert tutor'}`,
    "provider": {
      "@type": "Person",
      "name": teacher.full_name || "Professional Tutor",
      "jobTitle": "Tutor",
      "description": teacher.bio
    },
    "educationalLevel": teacher.grade_levels?.join(', '),
    "availableLanguage": ["English", "Arabic"],
    "offers": {
      "@type": "Offer",
      "price": teacher.hourly_rate,
      "priceCurrency": "BHD",
      "availability": "https://schema.org/InStock",
      "url": `https://taealam.bh/teacher/${teacher.uuid}`
    },
    "courseMode": "online",
    "teaches": teacher.subjects
  };
};

/**
 * Generate FAQ schema for featured snippets
 * Helps pages appear in Google's FAQ rich results
 *
 * @param {Array} faqs - Array of {question, answer} objects
 */
export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate HowTo schema for step-by-step guides
 * Useful for "How It Works" pages
 *
 * @param {Object} config - {name, description, steps}
 */
export const generateHowToSchema = (config) => {
  if (!config || !config.steps) return null;

  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": config.name,
    "description": config.description,
    "step": config.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      "url": step.url
    }))
  };
};

/**
 * Generate Person schema for teacher profiles
 *
 * @param {Object} teacher - Teacher data object
 */
export const generatePersonSchema = (teacher) => {
  if (!teacher) return null;

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": teacher.full_name,
    "description": teacher.bio,
    "jobTitle": "Educator",
    "worksFor": {
      "@type": "Organization",
      "name": "Taealam"
    },
    "knowsAbout": teacher.subjects,
    "alumniOf": teacher.education,
    "url": `https://taealam.bh/teacher/${teacher.uuid}`
  };
};

/**
 * Generate Local Business schema
 * Useful if Taealam has a physical office
 */
export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Taealam",
  "description": "Educational tutoring platform in Bahrain",
  "url": "https://taealam.bh",
  "telephone": "+973-XXXX-XXXX", // Replace with actual phone
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "BH",
    "addressRegion": "Bahrain"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 26.0667, // Bahrain approximate coordinates
    "longitude": 50.5577
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday"
    ],
    "opens": "00:00",
    "closes": "23:59"
  }
});
